/**
 * Spacing System
 * 4px base spacing system with semantic size names
 * Uses branded types to prevent direct string access
 */

// Branded type system to prevent direct access
declare const __spacing_brand: unique symbol;
declare const __raw_spacing_brand: unique symbol;

export interface SpacingToken {
  readonly [__spacing_brand]: true;
  readonly _value: string;
}

export interface RawSpacingToken {
  readonly [__raw_spacing_brand]: true;
  readonly _value: number;
}

// Internal factory functions
const createSpacingToken = (value: string): SpacingToken => ({ _value: value } as SpacingToken);

const createRawSpacingToken = (value: number): RawSpacingToken => ({ _value: value } as RawSpacingToken);

// Internal raw spacing values for foundation components (Radius, etc.)
export const rawSpacing = {
  0: createRawSpacingToken(0),
  1: createRawSpacingToken(1), // 4px
  2: createRawSpacingToken(2), // 8px
  3: createRawSpacingToken(3), // 12px
  4: createRawSpacingToken(4), // 16px
  5: createRawSpacingToken(5), // 20px
  6: createRawSpacingToken(6), // 24px
  8: createRawSpacingToken(8), // 32px
  10: createRawSpacingToken(10), // 40px
  12: createRawSpacingToken(12), // 48px
  16: createRawSpacingToken(16), // 64px
  20: createRawSpacingToken(20), // 80px
  24: createRawSpacingToken(24), // 96px
} as const;

// Public API - these are the ONLY ways to get spacing tokens
export const Spacing = {
  // Semantic spacing
  zero: createSpacingToken("zero"),
  tiny: createSpacingToken("tiny"),
  small: createSpacingToken("small"),
  medium: createSpacingToken("medium"),
  large: createSpacingToken("large"),
  extraLarge: createSpacingToken("extraLarge"),
  huge: createSpacingToken("huge"),
} as const;

// Internal function to resolve tokens to CSS values
export const resolveSpacing = (token: SpacingToken | RawSpacingToken): string => {
  if ("_value" in token && typeof token._value === "number") {
    return `${token._value * 4}px`;
  }

  const spacingMap: Record<string, string> = {
    zero: "0px", // 0 * 4px
    tiny: "4px", // 1 * 4px
    small: "8px", // 2 * 4px
    medium: "16px", // 4 * 4px
    large: "24px", // 6 * 4px
    extraLarge: "32px", // 8 * 4px
    huge: "48px", // 12 * 4px
  };

  return spacingMap[(token as SpacingToken)._value] || "0px";
};
